import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/sharp-solid-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  type FC,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import Typography from '../typography';

interface IProps {
  className?: string;
  title: ReactNode | string;
  summary: ReactNode | string;
  startOpened?: boolean;
  onClick?: (open: boolean) => void;
  titleClassName?: string;
  disabled?: boolean;
  blurValue?: boolean;
  isOpenOverride?: boolean;
}

const Accordion: FC<IProps> = ({
  className = '',
  disabled = false,
  startOpened = false,
  isOpenOverride = undefined,
  onClick = () => {},
  summary,
  title,
  titleClassName = '',
  blurValue = false,
}) => {
  const [open, setOpen] = useState(startOpened);

  const summaryIsString = useMemo(() => typeof summary === 'string', [summary]);

  const handleClickEvent = useCallback(() => {
    if (disabled) return;
    onClick?.(!open);
    if (isOpenOverride) return;

    setOpen((prev) => !prev);
  }, [disabled, onClick, open, isOpenOverride]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.code === 'Enter') handleClickEvent();
    },
    [handleClickEvent]
  );

  const isOpen = useMemo(() => isOpenOverride ?? open, [open, isOpenOverride]);

  return (
    <div
      className={twMerge(
        'flex flex-col border-y-1 border-gray-20 px-4 py-3',
        className
      )}
    >
      <div
        className="flex items-center justify-between"
        role="button"
        tabIndex={0}
        onClick={handleClickEvent}
        onKeyDown={handleKeyDown}
      >
        <Typography className={twMerge('pr-3', titleClassName)} variant="h6">
          {title}
        </Typography>
        <FontAwesomeIcon
          className={twMerge(
            isOpen ? ' rotate-0' : 'rotate-180',
            'transition-transform duration-300'
          )}
          icon={faChevronUp}
        />
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.section
            key="content"
            animate="open"
            exit="collapsed"
            initial="collapsed"
            transition={{ duration: 0.2 }}
            variants={{
              collapsed: { height: 0, opacity: 0 },
              open: { height: 'auto', opacity: 1 },
            }}
          >
            <div
              className={twMerge(
                'mb-3 mt-2',
                blurValue ? 'blur-sm' : 'blur-none'
              )}
            >
              {summaryIsString ? (
                <Typography variant="body2">{summary}</Typography>
              ) : (
                summary
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Accordion;
